import { z } from 'zod'
import { parseSleepInput } from './validation'
import type { SleepRecord, SleepDetails } from './types'

export const shortcutSleepSampleSchema = z.strictObject({
  startAt: z.iso.datetime({ offset: true }),
  endAt: z.iso.datetime({ offset: true }),
  stage: z.string().min(1).max(160),
  source: z.string().max(500),
  wakeTimeZone: z.string().min(1).max(80),
})
export type ShortcutSleepSample = z.infer<typeof shortcutSleepSampleSchema>
type Interval = { start: number; end: number; stage: string; zone: string; source: string }
const stageAliases: Record<string, string> = {
  '0': 'inBed', '1': 'asleep', '2': 'awake', '3': 'core', '4': 'deep', '5': 'rem',
  'in bed': 'inBed', inbed: 'inBed', 卧床: 'inBed', 卧床时间: 'inBed',
  awake: 'awake', 清醒: 'awake', 清醒时间: 'awake',
  asleep: 'asleep', 'asleep unspecified': 'asleep', asleepunspecified: 'asleep',
  睡眠: 'asleep', 睡着: 'asleep', 已入睡: 'asleep', 入睡: 'asleep', '睡眠（未指定）': 'asleep',
  core: 'core', 'asleep core': 'core', asleepcore: 'core', 核心: 'core', 核心睡眠: 'core',
  deep: 'deep', 'asleep deep': 'deep', asleepdeep: 'deep', 深度: 'deep', 深度睡眠: 'deep',
  rem: 'rem', 'asleep rem': 'rem', asleeprem: 'rem', 快速动眼: 'rem', 快速动眼睡眠: 'rem', 快速眼动: 'rem',
}
function decode(value: string): string {
  try { return decodeURIComponent(value).trim() }
  catch { throw new Error('无法识别睡眠阶段或来源') }
}
function union(items: Interval[]): Interval[] {
  const result: Interval[] = []
  for (const item of [...items].sort((a, b) => a.start - b.start)) {
    const last = result.at(-1)
    if (last && item.start <= last.end) last.end = Math.max(last.end, item.end)
    else result.push({ ...item })
  }
  return result
}

/** Partition intervals so duplicate or conflicting stages never count twice. */
function sleepDetails(selected: Interval[], awake: Interval[], start: number, end: number): SleepDetails {
  const relevant = [...selected, ...awake].filter(item => item.start < end && item.end > start)
  const boundaries = [...new Set([start, end, ...relevant.flatMap(item => [Math.max(start, item.start), Math.min(end, item.end)])])].sort((a, b) => a - b)
  const stageMinutes: SleepDetails['stageMinutes'] = {}
  let awakeMinutes = 0
  let hasAwake = false
  for (let index = 1; index < boundaries.length; index++) {
    const left = boundaries[index - 1]!, right = boundaries[index]!
    const minutes = (right - left) / 60_000
    if (awake.some(item => item.start < right && item.end > left)) {
      hasAwake = true
      awakeMinutes += minutes
      continue
    }
    const stages = new Set(selected.filter(item => item.start < right && item.end > left).map(item => item.stage))
    if (!stages.size) continue
    const stage = stages.size === 1 ? [...stages][0] : 'unspecified'
    const key = stage === 'core' || stage === 'deep' || stage === 'rem' ? stage : 'unspecified'
    stageMinutes[key] = (stageMinutes[key] ?? 0) + minutes
  }
  return { stageMinutes, ...(hasAwake ? { awakeMinutes } : {}) }
}

/** Prefer Watch sessions; explicitly retain unattributed samples when Shortcuts omits Source. */
export function shortcutSleepRecords(samples: ShortcutSleepSample[], exportedAt: string, now: Date): SleepRecord[] {
  const exportMs = Date.parse(exportedAt)
  const intervals = samples.map((sample): Interval => {
    const start = Date.parse(sample.startAt), end = Date.parse(sample.endAt)
    if (start < exportMs - 30 * 86400_000 || end > exportMs + 300_000) throw new Error('记录超出快照的最近 30 天范围')
    if (end <= start || end - start > 86400_000) throw new Error('睡眠片段时间无效')
    const label = decode(sample.stage).toLowerCase()
    if (!Object.hasOwn(stageAliases, label)) throw new Error('无法识别睡眠阶段或来源')
    const stage = stageAliases[label]!
    return { start, end, stage, source: decode(sample.source), zone: sample.wakeTimeZone }
  }).filter((item) => !item.source || /watch|手表/iu.test(item.source))
  if (samples.length && !intervals.length) throw new Error('未找到 Apple Watch 睡眠来源')
  const sources = new Map<string, Interval[]>()
  for (const item of intervals) {
    const key = `${item.source}\0${item.zone}`
    sources.set(key, [...(sources.get(key) ?? []), item])
  }
  const daily = new Map<string, SleepRecord>()
  for (const items of sources.values()) {
    const asleep = items.filter((item) => !['awake', 'inBed'].includes(item.stage)).sort((a, b) => a.start - b.start)
    const sessions: Interval[][] = []
    let end = -Infinity
    for (const item of asleep) {
      if (item.start - end > 3 * 3600_000) sessions.push([])
      sessions.at(-1)!.push(item)
      end = Math.max(end, item.end)
    }
    for (const session of sessions) {
      // Detailed Watch stages take precedence over overlapping generic asleep samples.
      const detailed = session.filter((item) => item.stage !== 'asleep')
      const merged = union(detailed.length ? detailed : session)
      const awake = union(items.filter((item) => item.stage === 'awake'))
      let milliseconds = 0
      for (const span of merged) {
        milliseconds += span.end - span.start
        for (const gap of awake) milliseconds -= Math.max(0, Math.min(span.end, gap.end) - Math.max(span.start, gap.start))
      }
      if (milliseconds < 60_000) continue
      const first = merged[0]!, last = merged.at(-1)!
      const record = parseSleepInput({ startAt: new Date(first.start).toISOString(), endAt: new Date(last.end).toISOString(), wakeTimeZone: first.zone }, [], now)
      record.durationMinutes = milliseconds / 60_000
      record.details = sleepDetails(detailed.length ? detailed : session, awake, first.start, last.end)
      record.source = 'shortcuts'
      record.id = record.externalId = `sleep:${first.source ? 'watch' : 'unattributed'}:${record.wakeLocalDay}`
      record.createdAt = record.updatedAt = exportedAt
      const previous = daily.get(record.wakeLocalDay)
      // Primary sleep only. Never add different devices or naps to the main night.
      const attributed = Boolean(first.source)
      const previousAttributed = Boolean(previous?.externalId?.startsWith('sleep:watch:'))
      if (!previous || (attributed && !previousAttributed) ||
          (attributed === previousAttributed && (record.durationMinutes > previous.durationMinutes ||
          (record.durationMinutes === previous.durationMinutes && record.endAt > previous.endAt)))) daily.set(record.wakeLocalDay, record)
    }
  }
  return [...daily.values()].sort((a, b) => a.endAt.localeCompare(b.endAt))
}
